import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Activity } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!t) return "—";
  const diff = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function platformBadge(p) {
  const key = String(p || "").toLowerCase();
  if (key.includes("tiktok")) return { label: "TikTok", color: "bg-rose-500/15 text-rose-500" };
  if (key.includes("youtube")) return { label: "YouTube", color: "bg-red-500/15 text-red-500" };
  if (key.includes("instagram")) return { label: "Instagram", color: "bg-fuchsia-500/15 text-fuchsia-500" };
  if (key.includes("twitter") || key === "x") return { label: "X", color: "bg-sky-500/15 text-sky-500" };
  return { label: p || "Video", color: "bg-zinc-500/15 text-zinc-500" };
}

export function ActivityFeed({ limit = 8 } = {}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/activity/global", { params: { limit } })
      .then((r) => { if (alive) setItems(r.data?.items || []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [limit]);

  return (
    <div
      data-testid="activity-feed-card"
      className="rounded-2xl border border-zinc-200/80 bg-white/80 p-5 backdrop-blur-sm dark:border-white/[0.06] dark:bg-white/[0.025]"
    >
      <div className="mb-4 flex items-center gap-2">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-zinc-100 text-zinc-700 dark:bg-white/[0.05] dark:text-zinc-300">
          <Activity size={14} />
        </span>
        <h3 className="text-[14px] font-semibold tracking-tight text-zinc-900 dark:text-white">
          Live Activity
        </h3>
      </div>

      <div className="relative space-y-2">
        {loading ? (
          <div className="py-10 text-center text-[12.5px] text-zinc-500 dark:text-zinc-500">
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center text-[12.5px] text-zinc-500 dark:text-zinc-500">
            No recent clip submissions yet.
          </div>
        ) : (
          items.slice(0, limit).map((it, idx) => {
            const pb = platformBadge(it.platform);
            const avatar = it.user_avatar || `https://api.dicebear.com/8.x/initials/svg?seed=${encodeURIComponent(it.user_name || "U")}&backgroundColor=c8ff00&textColor=000000`;
            // Fade the last 2 items
            const fadeOpacity = idx >= items.length - 2 ? "opacity-60" : "opacity-100";
            return (
              <div
                key={it.id || idx}
                data-testid={`activity-feed-item-${idx}`}
                className={`flex items-center gap-3 rounded-lg border border-transparent px-2 py-2 transition-colors hover:border-zinc-200/70 hover:bg-zinc-50/60 dark:hover:border-white/[0.06] dark:hover:bg-white/[0.025] ${fadeOpacity}`}
              >
                <img src={avatar} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[12.5px] text-zinc-700 dark:text-zinc-300">
                    <span className="font-semibold text-zinc-900 dark:text-white">@{it.user_name}</span>
                    <span className="text-zinc-500"> submitted on </span>
                    <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-bold ${pb.color}`}>{pb.label}</span>
                    <span className="text-zinc-500"> for </span>
                    <span className="font-semibold text-zinc-900 dark:text-white">{it.campaign_name}</span>
                  </div>
                </div>
                <span className="shrink-0 text-[11px] tabular-nums text-zinc-500 dark:text-zinc-500">
                  {timeAgo(it.created_at)}
                </span>
              </div>
            );
          })
        )}
        {/* Soft fade at the bottom hint */}
        <div className="pointer-events-none absolute inset-x-0 -bottom-1 h-8 bg-gradient-to-t from-white via-white/70 to-transparent dark:from-[#0a0a0a] dark:via-[#0a0a0a]/70" />
      </div>
    </div>
  );
}

export default ActivityFeed;
